<template>
  <!-- Add animated background -->
  <div class="animated-background">
    <div class="floating-elements">
      <span class="element code">{ }</span>
      <span class="element book">📚</span>
      <span class="element graduation">🎓</span>
      <span class="element tech">💻</span>
      <span class="element atom">⚛️</span>
      <span class="element formula">∑</span>
      <span class="element infinity">∞</span>
      <span class="element binary">01</span>
      <span class="element circuit"></span>
      <span class="element grid"></span>
    </div>
  </div>
  <div dir="rtl">
    <!-- First row: Page title -->
    <div class="mb-4">
      <h1>الاستراحات</h1>
    </div>
    
    <!-- Second row: Add button and search bar -->
    <div class="d-flex mb-4">
      <div style="width: 25%; padding-left: 16px;">
        <v-btn 
          color="primary" 
          @click="showAddDialog = true" 
          block
          height="56px"
          class="mt-n1"
        >
          <v-icon start>mdi-plus</v-icon>
          إضافة استراحة
        </v-btn>
      </div>
      <div style="width: 75%;">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="بحث"
          single-line
          hide-details
          class="rtl-input"
        ></v-text-field>
      </div>
    </div>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="breaks"
      :search="search"
      class="elevation-1"
    >
      <template v-slot:item="{ item }">
        <tr>
          <td class="text-center">{{ item.name }}</td>
          <td class="text-center">{{ item.price }}</td>
          <td class="text-center">{{ item.note }}</td>
          <td class="text-center">
            <v-icon
              :color="item.active ? 'green' : 'red'"
              @click="toggleBreakStatus(item)"
            >
              mdi-power
            </v-icon>
          </td>
          <td class="text-center">
            <v-icon small class="mr-2" @click="editBreak(item)" title="تعديل">mdi-pencil</v-icon>
            <v-icon small @click="showDeleteConfirm(item.id)" title="حذف">mdi-delete</v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Add Break Dialog -->
    <v-dialog v-model="showAddDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-right">إضافة استراحة جديدة</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newBreak.name"
            label="الاسم"
            class="rtl-input"
          ></v-text-field>
          <v-text-field
            v-model.number="newBreak.price"
            label="السعر"
            type="number"
            class="rtl-input"
          ></v-text-field>
          <v-textarea
            v-model="newBreak.note"
            label="ملاحظات"
            class="rtl-input"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="rtl-dialog">
          <v-btn color="error" text @click="showAddDialog = false">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="addNewBreak">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Break Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500px">
      <v-card class="rtl-dialog">
        <v-card-title class="text-right">تعديل الاستراحة</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="currentBreak.name"
            label="الاسم"
            class="rtl-input"
          ></v-text-field>
          <v-text-field
            v-model.number="currentBreak.price"
            label="السعر"
            type="number"
            class="rtl-input"
          ></v-text-field>
          <v-textarea
            v-model="currentBreak.note"
            label="ملاحظات"
            class="rtl-input"
          ></v-textarea>
        </v-card-text>
        <v-card-actions class="rtl-dialog">
          <v-btn color="error" text @click="showEditDialog = false">إلغاء</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="updateBreakDetails">حفظ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add confirmation dialog -->
    <v-dialog v-model="confirmDeleteDialog" max-width="400px">
      <v-card class="rtl-dialog">
        <v-card-title class="dialog-header">
          <span>تأكيد الحذف</span>
        </v-card-title>
        <v-card-text class="dialog-content">
          هل أنت متأكد من حذف استراحة القهوة؟
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="confirmDeleteDialog = false">إلغاء</v-btn>
          <v-btn color="error" variant="text" @click="confirmDelete">حذف</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import debounce from 'lodash/debounce';

export default {
  name: 'BreaksComponent',
  data() {
    return {
      search: '',
      isLoading: false,
      newBreak: {
        name: '',
        price: 0,
        note: '',
        active: 1
      },
      currentBreak: {},
      showAddDialog: false,
      showEditDialog: false,
      confirmDeleteDialog: false,
      breakToDelete: null,
      headers: [
        { title: 'الاسم', key: 'name', align: 'center' },
        { title: 'السعر', key: 'price', align: 'center' },
        { title: 'ملاحظات', key: 'note', align: 'center' },
        { title: 'الحالة', key: 'active', align: 'center' },
        { title: 'الإجراءات', key: 'actions', align: 'center', sortable: false }
      ]
    };
  },
  computed: {
    ...mapGetters(['allBreaks']),
    breaks() {
      return this.allBreaks;
    }
  },
  methods: {
    ...mapActions(['fetchBreaks', 'addBreak', 'updateBreak', 'deleteBreak']),
    
    editBreak(item) {
      this.currentBreak = { ...item };
      this.showEditDialog = true;
    },

    async toggleBreakStatus(item) {
      try {
        const breakData = { ...item };
        breakData.active = !breakData.active;
        await this.updateBreak(breakData);
        await this.refreshTable();
      } catch (error) {
        console.error('خطأ في تحديث حالة الاستراحة:', error);
      }
    },

    async addNewBreak() {
      try {
        await this.addBreak(this.newBreak);
        this.showAddDialog = false;
        await this.refreshTable();
        
        this.newBreak = {
          name: '',
          price: 0,
          note: '',
          active: 1
        };
      } catch (error) {
        console.error('خطأ في إضافة الاستراحة:', error);
      }
    },

    async updateBreakDetails() {
      try {
        await this.updateBreak(this.currentBreak);
        this.showEditDialog = false;
        await this.refreshTable();
      } catch (error) {
        console.error('خطأ في تحديث بيانات الاستراحة:', error);
      }
    },

    handleSearch: debounce(async function() {
      console.log('Search triggered with:', this.search);
      
      try {
        this.isLoading = true;
        if (!this.search || !this.search.trim()) {
          console.log('Empty search - fetching all breaks');
          await this.fetchBreaks();
        } else {
          const searchTerm = this.search.trim();
          console.log('Searching for:', searchTerm);
          await this.fetchBreaks({ name: searchTerm });
        }
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        this.isLoading = false;
      }
    }, 300),

    async refreshTable() {
      console.log('Refreshing table with search:', this.search);
      
      try {
        this.isLoading = true;
        if (this.search && this.search.trim()) {
          await this.fetchBreaks({ name: this.search.trim() });
        } else {
          await this.fetchBreaks();
        }
      } catch (error) {
        console.error('خطأ في تحديث الجدول:', error);
      } finally {
        this.isLoading = false;
      }
    },

    showDeleteConfirm(id) {
      this.breakToDelete = id;
      this.confirmDeleteDialog = true;
    },
    
    confirmDelete() {
      this.deleteBreak(this.breakToDelete);
      this.confirmDeleteDialog = false;
    }
  },
  async created() {
    try {
      await this.refreshTable();
    } catch (error) {
      console.error('خطأ في تهيئة الصفحة:', error);
    }
  },
  watch: {
    search: {
      handler(newVal) {
        console.log('Search query changed:', newVal);
        this.handleSearch();
      }
    }
  }
};
</script>

<!-- Breaks styles are now handled globally in src/assets/styles/global.css -->
